import { Link } from "@/lib/nav";

import FilterChips from "@/components/filter-chips";
import ListRowCard from "@/components/list-card";
import ListSearch from "@/components/list-search";
import Pager from "@/components/pager";
import Pill from "@buildpy/ui/pill";
import ResponsiveList from "@/components/responsive-list";
import ListTable from "@buildpy/ui/list-table";
import {
  TableCell,
  TableRow,
} from "@buildpy/ui/table";
import { listHref } from "@buildpy/core/modules/list/search";
import { QUOTE_STATUS_LABEL, QUOTE_STATUSES } from "@buildpy/core/modules/quote/types";
import Money from "@/components/money";
import { formatDate } from "@/modules/quote/ui/format";
import QuoteStatusBadge from "@/modules/quote/ui/status-badge";
import type { QuotesView } from "@buildpy/core/views/g1b/quotes";
import PageHeader from "@/shell/page-header";

// 견적 목록 — 칩 숫자 = 검색어·최신 보기까지 건 상태별 건수, 줄은 한 쪽씩.
const Page = ({ data }: { data: QuotesView }) => {
  const { q, status, latest, result, customers, members } = data;

  const quotes = result.rows;
  const customerName = new Map(customers.map((c) => [c.id, c.name]));
  const memberName = new Map(members.map((m) => [m.id, m.name]));

  // 칩·쪽 링크는 검색어·최신 버전 보기를 싣고 간다(칩 숫자 = 누른 뒤 건수) — 칩을 누르면 쪽은 1로.
  const href = (s: string | null, page?: number) =>
    listHref("/app/quotes", { q, status: s, latest: latest ? "1" : null, page });
  const chips = [
    { href: href(null), label: "전체", count: result.counts.ALL },
    ...QUOTE_STATUSES.map((s) => ({
      href: href(s),
      label: QUOTE_STATUS_LABEL[s],
      count: result.counts[s],
    })),
  ];

  const emptyText = latest
    ? q
      ? "답을 기다리는 견적의 최신 버전 중 검색어에 맞는 견적이 없습니다."
      : "답을 기다리는 견적의 최신 버전 중 해당하는 견적이 없습니다."
    : q
      ? "검색어에 맞는 견적이 없습니다."
      : "견적이 없습니다.";

  // 폰(md 미만)은 한 행 = 카드 한 장 — 표는 금액·상태 열이 화면 밖으로 밀렸다.
  const cards = quotes.map((quote) => (
    <ListRowCard
      key={quote.id}
      amount={<Money value={quote.total} />}
      badge={<QuoteStatusBadge status={quote.status} />}
      href={`/app/quotes/${quote.id}`}
      meta={[
        `${quote.quote_no} v${quote.version}`,
        quote.sent_at ? `발송 ${formatDate(quote.sent_at)}` : "미발송",
        memberName.get(quote.created_by),
      ]}
      subtitle={quote.title}
      tag={quote.kind === "ROUGH" && <Pill>가견적</Pill>}
      testId={`quote-card-${quote.id}`}
      title={customerName.get(quote.customer_id) ?? "—"}
    />
  ));

  return (
    <>
      <PageHeader meta={`${result.total}건`} title="견적" />

      <div className="flex flex-col gap-y-3">
        <ListSearch
          action="/app/quotes"
          aria-label="견적 검색"
          keep={{ status, latest: latest ? "1" : null }}
          nameMatchCapped={result.nameMatchCapped}
          placeholder="견적번호 · 공사명 · 고객 이름"
          q={q}
        />
        <FilterChips activeHref={href(status)} aria-label="견적 상태" items={chips} />
        {latest && (
          <p className="text-sm text-muted-foreground" data-testid="quotes-latest">
            답을 기다리는 견적의 최신 버전만 봅니다(가견적 제외) ·{" "}
            <Link className="underline underline-offset-2" href={listHref("/app/quotes", { q, status })}>
              모든 버전 보기
            </Link>
          </p>
        )}
      </div>

      <ResponsiveList
        cards={cards}
        empty={emptyText}
        label="견적 목록"
        testId="quote-cards"
        table={
          <ListTable
            columns={[
              { label: "번호" },
              { label: "고객" },
              { label: "공사명", wrap: true },
              { label: "상태" },
              { label: "최종 견적금액", align: "right" },
              { label: "발송일" },
              { label: "담당" },
            ]}
            empty={emptyText}>
            {quotes.map((quote) => (
              <TableRow key={quote.id} className="relative">
                <TableCell className="whitespace-nowrap px-3 py-2 font-medium">
                  <Link
                    className="after:absolute after:inset-0"
                    href={`/app/quotes/${quote.id}`}>
                    {quote.quote_no}
                    <span className="text-muted-foreground">
                      {" "}
                      v{quote.version}
                    </span>
                  </Link>
                  {/* 가견적은 금액의 성격이 다르다 — 목록에서 바로 갈라 보이게. */}
                  {quote.kind === "ROUGH" && (
                    <span className="ml-1.5 rounded-md bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                      가견적
                    </span>
                  )}
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2">
                  {customerName.get(quote.customer_id) ?? "—"}
                </TableCell>
                <TableCell className="px-3 py-2">{quote.title}</TableCell>
                <TableCell className="px-3 py-2">
                  <QuoteStatusBadge status={quote.status} />
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                  <Money value={quote.total} />
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                  {formatDate(quote.sent_at)}
                </TableCell>
                <TableCell className="whitespace-nowrap px-3 py-2">
                  {memberName.get(quote.created_by) ?? "—"}
                </TableCell>
              </TableRow>
            ))}
          </ListTable>
        }
      />

      <Pager hrefFor={(page) => href(status, page)} page={result.page} pageCount={result.pageCount} />
    </>
  );
};

export default Page;
